import { HomeIcon } from "@heroicons/react/20/solid";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { DashboardRoutes } from "../../routes/dashboard";
import { safeString } from "../Utils";

/**
 * Recursively flattens all routes including nested children
 * Returns an array of all route paths that have an element (are navigable)
 */
const flattenRoutes = (routes) => {
  const flattened = [];
  routes.forEach((r) => {
    if (r.children && r.children.length > 0) {
      // Add parent route if it has an element
      if (r.element) {
        flattened.push(r.path);
      }
      // Recursively flatten children
      flattened.push(...flattenRoutes(r.children));
    } else {
      // Leaf route - only add if it has an element
      if (r.element) {
        flattened.push(r.path);
      }
    }
  });
  return flattened;
};

/**
 * Checks if a route path exists in the DashboardRoutes configuration
 */
const routeExists = (path, allRoutes) => {
  if (!path) return false;

  // Normalize both the input path and routes for comparison
  const normalizeForComparison = (p) => {
    if (!p) return "";
    // Remove trailing slash for comparison (except root)
    return p === "/" ? "/" : p.replace(/\/$/, "");
  };

  const normalizedPath = normalizeForComparison(path);
  
  // Check if any route matches (with or without trailing slash)
  return allRoutes.some(route => {
    const normalizedRoute = normalizeForComparison(route);
    return normalizedRoute === normalizedPath;
  });
};

const Breadcrumbs = (props) => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const { currentPageTitle } = props;

  // Memoize the flattened routes list to avoid recalculating on every render
  const allValidRoutes = useMemo(() => {
    return flattenRoutes(DashboardRoutes);
  }, []);

  return (
    <nav
      className="hidden sm:flex border-b border-t border-gray-200 bg-white"
      aria-label="Breadcrumb"
    >
      <ol
        role="list"
        className="mx-auto flex w-full max-w-screen-xl space-x-4 px-4 sm:px-6 lg:px-8"
      >
        <li className="flex">
          <div className="flex items-center">
            <Link to="/dashboard/" className="text-gray-400 hover:text-gray-500">
              <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span className="sr-only">Home</span>
            </Link>
          </div>
        </li>
        {pathnames.length > 0 && (
          <>
            {pathnames.map((name, index) => {
              const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
              const isLast = index === pathnames.length - 1;
              const formattedName = name
                .replace(/-/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase());
              
              // Check if this route exists and is not the last item
              const routeExistsForPath = !isLast && routeExists(routeTo, allValidRoutes);

              return (
                <div key={index} className="flex items-center">
                  <svg
                    className="h-full w-6 flex-shrink-0 text-gray-200"
                    viewBox="0 0 24 44"
                    preserveAspectRatio="none"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                  </svg>
                  {isLast ? (
                    <li
                      key={name}
                      className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                      <div className="flex items-center">
                        {safeString(currentPageTitle) || formattedName}
                      </div>
                    </li>
                  ) : routeExistsForPath ? (
                    <Link
                      to={routeTo}
                      className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                      {formattedName}
                    </Link>
                  ) : (
                    <span className="ml-4 text-sm font-medium text-gray-400">
                      {formattedName}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
